import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  HiOutlineMenu,
  HiOutlineSearch,
  HiOutlinePlus,
  HiOutlineViewGrid,
  HiOutlineDocumentText,
  HiOutlineBookOpen,
  HiOutlineBookmark,
  HiOutlineScale,
  HiOutlineRefresh,
  HiOutlineCheck,
  HiOutlineEye,
  HiOutlineTicket,
  HiOutlineCog,
  HiOutlineX,
  HiOutlineLockClosed,
  HiOutlineUsers,
} from "react-icons/hi";

import Dropdown from "components/dropdown";
import IncompleteAppSettingsModal from "components/IncompleteAppSettingsModal";
import { useAppSettingsCompletionCheck } from "hooks/useAppSettingsCompletionCheck";
import { useAuthStore } from "store/authStore";
import { logout } from "api/auth";

import headerBell from "assets/svg/header-bell.svg";
import headerUserAvatar from "assets/svg/header-user-avatar.svg";
import headerChevronDown from "assets/svg/header-chevron-down.svg";

/** First step of new application (matches All New Applications → Create New). */
const CREATE_NEW_APPLICATION_PATH =
  "/dashboard/applications/new-application/discussion/preliminary-discussion-with-the-client";

type NavIconItem = {
  icon: typeof HiOutlinePlus;
  to: string | null;
  /** Active when pathname.startsWith — use for multi-step flows */
  activePrefix?: string;
  /** Active when pathname equals exactly — use when `to` is a prefix of other routes */
  activeExact?: string;
};

const NAV_ICONS: NavIconItem[] = [
  {
    icon: HiOutlinePlus,
    to: CREATE_NEW_APPLICATION_PATH,
    activePrefix: "/dashboard/applications/new-application",
  },
  { icon: HiOutlineViewGrid, to: "/dashboard/home" },
  {
    icon: HiOutlineDocumentText,
    to: "/dashboard/applications",
    activeExact: "/dashboard/applications",
  },
  {
    icon: HiOutlineBookOpen,
    to: "/dashboard/follow-ups",
    activeExact: "/dashboard/follow-ups",
  },
  {
    icon: HiOutlineScale,
    to: "/dashboard/opposition",
    activePrefix: "/dashboard/opposition",
  },
  {
    icon: HiOutlineBookmark,
    to: "/dashboard/assignments",
    activePrefix: "/dashboard/assignments",
  },
  {
    icon: HiOutlineRefresh,
    to: "/dashboard/renewal",
    activePrefix: "/dashboard/renewal",
  },
  {
    icon: HiOutlineCheck,
    to: "/dashboard/legal-certificate",
    activePrefix: "/dashboard/legal-certificate",
  },
  {
    icon: HiOutlineEye,
    to: "/dashboard/surveillance",
    activePrefix: "/dashboard/surveillance",
  },
  {
    icon: HiOutlineTicket,
    to: "/dashboard/reports/invoices",
    activePrefix: "/dashboard/reports/invoices",
  },
];

/** Labels for mobile drawer (matches NAV_ICONS order). */
const MOBILE_NAV_LABELS = [
  "Create New Application",
  "Dashboard",
  "New Applications",
  "Follow Up",
  "Opposition",
  "Assignments",
  "Renewal",
  "Legal Certificate",
  "Surveillance",
  "Invoice Report",
] as const;

function getRoleFromAccessToken(token: string | null): string | null {
  if (!token) return null;
  try {
    const base64 = token.split(".")[1];
    if (!base64) return null;
    const json = atob(base64.replace(/-/g, "+").replace(/_/g, "/"));
    const payload = JSON.parse(json) as { role?: unknown };
    return typeof payload.role === "string" ? payload.role : null;
  } catch {
    return null;
  }
}

function isNavIconActive(pathname: string, item: NavIconItem): boolean {
  if (!item.to) return false;
  if (item.activeExact !== undefined) return pathname === item.activeExact;
  if (item.activePrefix !== undefined) return pathname.startsWith(item.activePrefix);
  return pathname.startsWith(item.to);
}

interface DashboardLayoutProps {
  children: React.ReactNode;
  /** Left side of header, e.g. "New Application" or "All New Applications" */
  headerTitle?: string;
  /** Optional subtitle/brand, e.g. "UPRIGHT LEGAL" */
  headerSubtitle?: string;
  /** Shown after header title when viewing a specific lead (e.g. lead number from API) */
  headerLeadNo?: string | null;
  /** When set, shows brand label + divider before header title (dashboard home breadcrumb) */
  headerBrand?: string;
  /** When true, hide the layout header on mobile (child renders its own tabs + header to match Figma) */
  hideHeaderOnMobile?: boolean;
}

export default function DashboardLayout({
  children,
  headerTitle = "New Application",
  headerSubtitle,
  headerLeadNo,
  headerBrand,
  hideHeaderOnMobile = false,
}: DashboardLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const accessToken = useAuthStore((s) => s.accessToken);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const role = getRoleFromAccessToken(accessToken);
  const isSuperAdmin = role === "SUPER_ADMIN";
  const canEditAppSettings = role === "SUPER_ADMIN" || role === "ADMIN";

  const handleLogout = async () => {
    try {
      if (accessToken) await logout(accessToken);
    } finally {
      clearAuth();
      navigate("/auth/sign-in", { replace: true });
    }
  };
  const isSettingsActive = location.pathname.startsWith("/dashboard/app-settings");
  const isChangePasswordActive = location.pathname.startsWith("/dashboard/change-password");
  const isUsersActive = location.pathname.startsWith("/dashboard/users");

  const { incomplete: appSettingsIncomplete, dismiss: dismissAppSettingsPrompt } =
    useAppSettingsCompletionCheck(canEditAppSettings && !isSettingsActive);

  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileNavOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [mobileNavOpen]);

  const closeMobileNav = () => setMobileNavOpen(false);

  return (
    <div className="flex h-screen bg-[#f7f7f7] overflow-hidden">
      <aside
        className={`bg-white border-r border-[#EEEEEE] hidden md:flex flex-col py-4 gap-2 flex-shrink-0 transition-[width] duration-200 ease-out ${
          sidebarExpanded ? "w-[240px] items-stretch px-2" : "w-[68px] items-center"
        }`}
      >
        <button
          type="button"
          aria-label={sidebarExpanded ? "Collapse menu" : "Expand menu"}
          aria-expanded={sidebarExpanded}
          onClick={() => setSidebarExpanded((v) => !v)}
          className={`flex items-center h-[41px] rounded-lg mb-2 hover:bg-[#F7F7F7] transition-colors ${
            sidebarExpanded ? "justify-start px-2.5" : "justify-center w-[40px]"
          }`}
        >
          <HiOutlineMenu className="w-6 h-6 text-[#7A89A1] shrink-0" strokeWidth={1.5} />
          {sidebarExpanded && (
            <span className="ml-3 text-sm font-semibold text-[#242424]">Menu</span>
          )}
        </button>
        <div
          className={`flex items-center h-[40px] rounded-lg border border-[#E3EAEF] ${
            sidebarExpanded ? "justify-start px-2.5 gap-2" : "justify-center w-[40px]"
          }`}
        >
          <HiOutlineSearch className="w-[18px] h-[18px] text-black shrink-0" strokeWidth={1.5} />
          {sidebarExpanded && <span className="text-sm text-[#9ca3af]">Search</span>}
        </div>
        <div className="w-full h-px bg-[#171E29] my-2" />
        <div className={`flex flex-col gap-0.5 w-full ${sidebarExpanded ? "px-0" : "px-[14px]"}`}>
          {NAV_ICONS.map((item, index) => {
            const Icon = item.icon;
            const isActive = isNavIconActive(location.pathname, item);
            const label = MOBILE_NAV_LABELS[index] ?? `Nav ${index + 1}`;

            if (sidebarExpanded) {
              if (!item.to) {
                return (
                  <div
                    key={index}
                    className="flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-sm text-[#9ca3af]"
                  >
                    <Icon className="w-5 h-5 flex-shrink-0 text-[#9ca3af]" strokeWidth={1.5} />
                    <span>{label}</span>
                  </div>
                );
              }
              return (
                <button
                  key={index}
                  type="button"
                  aria-label={index === 0 ? "Create new application" : label}
                  onClick={() => navigate(item.to!)}
                  className={`flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left text-sm transition-colors ${
                    isActive ? "bg-[#222] font-medium text-white" : "text-[#222] hover:bg-[#F7F7F7]"
                  }`}
                >
                  <Icon
                    className="w-5 h-5 flex-shrink-0"
                    style={{ color: isActive ? "white" : "#222" }}
                    strokeWidth={1.5}
                  />
                  <span className="min-w-0 truncate">{label}</span>
                </button>
              );
            }

            const content = (
              <div
                className={`flex items-center justify-center h-9 w-10 rounded-lg ${
                  isActive ? "bg-[#222]" : ""
                }`}
              >
                <Icon
                  className="w-5 h-5 flex-shrink-0"
                  style={{ color: isActive ? "white" : "black" }}
                  strokeWidth={1.5}
                />
              </div>
            );
            return (
              <div key={index} className="group relative flex justify-center">
                {item.to ? (
                  <button
                    type="button"
                    aria-label={index === 0 ? "Create new application" : label}
                    onClick={() => navigate(item.to!)}
                    className="flex items-center justify-center w-10 rounded-lg hover:bg-[#F7F7F7] transition-colors"
                  >
                    {content}
                  </button>
                ) : (
                  content
                )}
                <span
                  role="tooltip"
                  className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 whitespace-nowrap rounded-md bg-[#222222] px-2 py-1 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100"
                >
                  {label}
                </span>
              </div>
            );
          })}
        </div>
        <div className={`group relative mt-auto flex ${sidebarExpanded ? "w-full" : "justify-center"}`}>
          <button
            type="button"
            aria-label="App Settings"
            onClick={() => navigate("/dashboard/app-settings")}
            className={`flex items-center rounded-lg transition-colors ${
              sidebarExpanded
                ? `w-full gap-3 px-2.5 py-2 text-sm ${
                    isSettingsActive ? "bg-[#222] font-medium text-white" : "text-[#222] hover:bg-[#F7F7F7]"
                  }`
                : `h-9 w-10 justify-center ${isSettingsActive ? "bg-[#222222]" : "hover:bg-[#F7F7F7]"}`
            }`}
          >
            <HiOutlineCog
              className={`w-5 h-5 shrink-0 ${isSettingsActive ? "text-white" : "text-[#222]"}`}
              strokeWidth={1.5}
            />
            {sidebarExpanded && <span>App settings</span>}
          </button>
          {!sidebarExpanded && (
            <span
              role="tooltip"
              className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 whitespace-nowrap rounded-md bg-[#222222] px-2 py-1 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100"
            >
              App Settings
            </span>
          )}
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {!(hideHeaderOnMobile ?? false) && (
          <>
            <header className="md:hidden h-[65px] bg-white border-b border-[#e5e7eb] flex items-center gap-3 px-4 flex-shrink-0">
              <button
                type="button"
                aria-label="Open menu"
                aria-expanded={mobileNavOpen}
                aria-controls="dashboard-mobile-nav"
                onClick={() => setMobileNavOpen(true)}
                className="flex items-center justify-center rounded-[10px] w-9 h-9 min-w-[36px] min-h-[36px] hover:bg-[#f7f7f7] touch-manipulation"
              >
                <HiOutlineMenu className="w-5 h-5 text-[#222]" strokeWidth={1.5} />
              </button>
              <div className="flex-1 min-w-0 font-medium text-sm leading-5 text-[#242424]">{headerTitle}</div>
            </header>

            {mobileNavOpen && (
              <div className="md:hidden fixed inset-0 z-[100] flex" role="dialog" aria-modal="true" aria-label="Navigation menu">
                <button
                  type="button"
                  aria-label="Close menu"
                  className="absolute inset-0 z-0 bg-black/40 touch-manipulation"
                  onClick={closeMobileNav}
                />
                <nav
                  id="dashboard-mobile-nav"
                  className="relative z-10 flex h-full w-[min(288px,88vw)] flex-col bg-white shadow-xl"
                >
                  <div className="flex h-[65px] shrink-0 items-center justify-between border-b border-[#e5e7eb] px-4">
                    <span className="font-semibold text-sm text-[#242424]">Menu</span>
                    <button
                      type="button"
                      aria-label="Close menu"
                      onClick={closeMobileNav}
                      className="flex size-9 items-center justify-center rounded-[10px] hover:bg-[#f7f7f7] touch-manipulation"
                    >
                      <HiOutlineX className="size-5 text-[#222]" strokeWidth={1.5} />
                    </button>
                  </div>
                  <div className="min-h-0 flex-1 overflow-y-auto px-2 py-3">
                    {NAV_ICONS.map((item, index) => {
                      const Icon = item.icon;
                      const isActive = isNavIconActive(location.pathname, item);
                      const label = MOBILE_NAV_LABELS[index] ?? `Nav ${index + 1}`;
                      if (!item.to) {
                        return (
                          <div
                            key={index}
                            className="mb-1 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-[#9ca3af]"
                          >
                            <Icon className="size-5 shrink-0 text-[#9ca3af]" strokeWidth={1.5} />
                            <span>{label}</span>
                          </div>
                        );
                      }
                      return (
                        <button
                          key={index}
                          type="button"
                          aria-label={index === 0 ? "Create new application" : undefined}
                          onClick={() => {
                            navigate(item.to!);
                            closeMobileNav();
                          }}
                          className={`mb-1 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm touch-manipulation transition-colors ${
                            isActive ? "bg-[#222] font-medium text-white" : "text-[#222] hover:bg-[#f7f7f7]"
                          }`}
                        >
                          <Icon
                            className="size-5 shrink-0"
                            strokeWidth={1.5}
                            style={isActive ? { color: "white" } : { color: "#222" }}
                          />
                          <span className="min-w-0 truncate">{label}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="shrink-0 border-t border-[#e5e7eb] p-2">
                    <button
                      type="button"
                      onClick={() => {
                        navigate("/dashboard/app-settings");
                        closeMobileNav();
                      }}
                      className={`mb-1 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm touch-manipulation transition-colors ${
                        isSettingsActive ? "bg-[#222] font-medium text-white" : "text-[#222] hover:bg-[#f7f7f7]"
                      }`}
                    >
                      <HiOutlineCog
                        className={`size-5 shrink-0 ${isSettingsActive ? "text-white" : "text-[#222]"}`}
                        strokeWidth={1.5}
                      />
                      <span>App settings</span>
                    </button>
                    {isSuperAdmin ? (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            navigate("/dashboard/users");
                            closeMobileNav();
                          }}
                          className={`mb-1 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm touch-manipulation transition-colors ${
                            isUsersActive ? "bg-[#222] font-medium text-white" : "text-[#222] hover:bg-[#f7f7f7]"
                          }`}
                        >
                          <HiOutlineUsers
                            className={`size-5 shrink-0 ${isUsersActive ? "text-white" : "text-[#222]"}`}
                            strokeWidth={1.5}
                            aria-hidden
                          />
                          <span>Users</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            navigate("/dashboard/change-password");
                            closeMobileNav();
                          }}
                          className={`mb-1 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm touch-manipulation transition-colors ${
                            isChangePasswordActive ? "bg-[#222] font-medium text-white" : "text-[#222] hover:bg-[#f7f7f7]"
                          }`}
                        >
                          <HiOutlineLockClosed
                            className={`size-5 shrink-0 ${isChangePasswordActive ? "text-white" : "text-[#222]"}`}
                            strokeWidth={1.5}
                            aria-hidden
                          />
                          <span>Change password</span>
                        </button>
                      </>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => {
                        closeMobileNav();
                        void handleLogout();
                      }}
                      className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-red-600 hover:bg-red-50 touch-manipulation"
                    >
                      Log out
                    </button>
                  </div>
                </nav>
              </div>
            )}
          </>
        )}
        <header className="hidden md:flex bg-white border-b border-[#eee] items-center justify-between py-2 flex-shrink-0 min-h-[56px]">
          <div className="flex flex-wrap items-center gap-y-2 rounded-lg shrink-0">
            {headerBrand ? (
              <div className="flex items-center gap-2 px-3 py-1 rounded-xl shrink-0">
                <span className="font-bold text-sm text-black tracking-tight leading-5">{headerBrand}</span>
                <div className="w-px h-[19px] bg-[#e5e5e5] shrink-0" />
                <span className="text-sm text-black font-normal leading-5">{headerTitle}</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-4 flex-wrap">
                {headerSubtitle && (
                  <div className="text-xl font-bold tracking-wider">{headerSubtitle}</div>
                )}
                <div className="flex items-center gap-2 text-sm text-black min-w-0">
                  <span className="shrink-0">{headerTitle}</span>
                  {headerLeadNo ? (
                    <>
                      <span className="text-[#CCCCCC] shrink-0" aria-hidden>
                        |
                      </span>
                      <span className="text-[#555] truncate" title={headerLeadNo}>
                        {headerLeadNo}
                      </span>
                    </>
                  ) : null}
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center gap-0.5 pr-3 shrink-0">
            <button
              type="button"
              aria-label="Notifications"
              className="flex items-center justify-center min-h-7 min-w-7 p-1 rounded-xl hover:bg-black/[0.04]"
            >
              <img alt="" src={headerBell} className="w-7 h-7" />
            </button>
            <Dropdown
              button={
                <div className="flex items-center gap-2 p-2 rounded-lg hover:bg-black/[0.04] cursor-pointer w-[180px]">
                  <div className="rounded-full shrink-0 size-6 overflow-hidden bg-black/[0.04]">
                    <img alt="" src={headerUserAvatar} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm text-black leading-5 truncate">ByeWind</p>
                  </div>
                  <img alt="" src={headerChevronDown} className="size-5 shrink-0" />
                </div>
              }
              children={
                <div className="flex flex-col gap-1 rounded-lg border border-[#eee] bg-white p-2 shadow-lg min-w-[180px]">
                  {isSuperAdmin ? (
                    <>
                      <button
                        type="button"
                        onClick={() => navigate("/dashboard/users")}
                        className={`text-left rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                          isUsersActive
                            ? "bg-[#F7F7F7] text-[#222]"
                            : "text-[#222] hover:bg-[#F7F7F7]"
                        }`}
                      >
                        Users
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate("/dashboard/change-password")}
                        className={`text-left rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                          isChangePasswordActive
                            ? "bg-[#F7F7F7] text-[#222]"
                            : "text-[#222] hover:bg-[#F7F7F7]"
                        }`}
                      >
                        Change password
                      </button>
                    </>
                  ) : null}
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="rounded-md px-3 py-2 text-left text-sm font-medium text-red-500 hover:bg-red-50"
                  >
                    Log Out
                  </button>
                </div>
              }
              classNames="top-full right-0 mt-2 origin-top-right"
              animation="origin-top-right transition-all duration-200 ease-out"
            />
          </div>
        </header>

        {children}
      </div>

      <IncompleteAppSettingsModal
        open={appSettingsIncomplete}
        onDismiss={dismissAppSettingsPrompt}
        onComplete={() => {
          dismissAppSettingsPrompt();
          navigate("/dashboard/app-settings");
        }}
      />
    </div>
  );
}
